import { getDeadlines } from '../lib/tax'
import { Calendar, CheckCircle, Clock, AlertCircle, CreditCard, Info, HelpCircle } from 'lucide-react'
import { differenceInDays } from 'date-fns'
import { useState } from 'react'

export default function Timeline() {
  const deadlines = getDeadlines()
  const now = new Date()
  const [showPoaExplainer, setShowPoaExplainer] = useState(false)

  function daysUntil(date) { return differenceInDays(date, now) }
  function status(date) {
    const d = daysUntil(date)
    if (d < 0)  return 'past'
    if (d <= 14) return 'urgent'
    if (d <= 60) return 'upcoming'
    return 'future'
  }

  const statusConfig = {
    past:     { color:'var(--slate-dim)', badge:'badge-slate',  icon: CheckCircle,  dot:'#3D5A72',         label:'Filed' },
    urgent:   { color:'var(--red)',       badge:'badge-red',    icon: AlertCircle,  dot:'var(--red)',       label:'Due soon' },
    upcoming: { color:'var(--amber)',     badge:'badge-amber',  icon: Clock,        dot:'var(--amber)',     label:'Upcoming' },
    future:   { color:'var(--slate)',     badge:'badge-slate',  icon: Calendar,     dot:'var(--navy-border)', label:'Scheduled' },
  }

  const nextDeadline = deadlines.find(d => daysUntil(d.date) >= 0)

  return (
    <div className="fade-up">
      <div className="page-head">
        <h1 className="page-title">Tax Timeline</h1>
        <p className="page-sub">Every HMRC deadline for the 2026/27 tax year — in one place</p>
      </div>

      {/* Next deadline hero */}
      {nextDeadline && (
        <div className="card" style={{ marginBottom:24, background:'linear-gradient(135deg,var(--navy-card),#0a2040)', borderColor: daysUntil(nextDeadline.date) <= 14 ? 'rgba(255,92,92,.3)' : 'rgba(0,212,170,.2)' }}>
          <div style={{ display:'flex', justifyContent:'space-between', alignItems:'flex-start' }}>
            <div>
              <div style={{ fontSize:11, color:'var(--slate)', textTransform:'uppercase', letterSpacing:'.08em', marginBottom:8 }}>Next Deadline</div>
              <div style={{ fontFamily:'var(--font-display)', fontSize:26, marginBottom:4 }}>
                {nextDeadline.label || `${nextDeadline.q} Quarterly Filing`}
              </div>
              <div style={{ fontSize:13, color:'var(--slate)', marginBottom:12 }}>{nextDeadline.period}</div>
              <div style={{ display:'flex', gap:12, alignItems:'center' }}>
                <span className={`badge ${daysUntil(nextDeadline.date) <= 14 ? 'badge-red' : 'badge-teal'}`}>
                  {daysUntil(nextDeadline.date)} days away
                </span>
                <span style={{ fontSize:13, color:'var(--slate)' }}>Due: <strong style={{color:'var(--white)'}}>{nextDeadline.deadline}</strong></span>
              </div>
            </div>
            <div style={{ textAlign:'right', flexShrink:0, marginLeft:20 }}>
              <div style={{ fontFamily:'var(--font-display)', fontSize:56, color: daysUntil(nextDeadline.date) <= 14 ? 'var(--red)' : 'var(--teal)', lineHeight:1 }}>
                {daysUntil(nextDeadline.date)}
              </div>
              <div style={{ fontSize:12, color:'var(--slate)' }}>days</div>
            </div>
          </div>
        </div>
      )}

      <div className="g2 timeline-grid">
        {/* Quarterly submissions */}
        <div>
          <div style={{ fontSize:13, fontWeight:600, color:'var(--teal)', marginBottom:14, display:'flex', alignItems:'center', gap:7 }}>
            <Calendar size={14} /> Quarterly HMRC Filings
          </div>
          <div className="card" style={{ marginBottom:14 }}>
            {deadlines.filter(d => d.type === 'submission').map((d, i) => {
              const s = status(d.date)
              const cfg = statusConfig[s]
              const days = daysUntil(d.date)
              return (
                <div key={d.q} className="timeline-item">
                  <div className="timeline-dot" style={{ background:cfg.dot, marginTop:5 }} />
                  <div style={{ flex:1 }}>
                    <div style={{ display:'flex', justifyContent:'space-between', alignItems:'flex-start', marginBottom:4 }}>
                      <div style={{ fontWeight:600, fontSize:14 }}>{d.q} — Quarterly Filing</div>
                      <span className={`badge ${cfg.badge}`} style={{ fontSize:10 }}>
                        {s === 'past' ? 'Filed' : `${days}d`}
                      </span>
                    </div>
                    <div style={{ fontSize:12, color:'var(--slate)', marginBottom:2 }}>Period: {d.period}</div>
                    <div style={{ fontSize:12 }}>
                      <span style={{ color:'var(--slate)' }}>Deadline: </span>
                      <span style={{ color: s === 'urgent' ? 'var(--red)' : s === 'past' ? 'var(--slate-dim)' : 'var(--white)', fontWeight:600 }}>{d.deadline}</span>
                    </div>
                    {s === 'past' && <div style={{ fontSize:11, color:'var(--slate-dim)', marginTop:4, display:'flex', alignItems:'center', gap:4 }}><CheckCircle size={11} /> Deadline passed</div>}
                    {s === 'urgent' && <div style={{ fontSize:11, color:'var(--red)', marginTop:4, display:'flex', alignItems:'center', gap:4 }}><AlertCircle size={11} /> Action required — {days} days left</div>}
                  </div>
                </div>
              )
            })}
          </div>
          <div className="alert alert-amber" style={{ flexDirection:'column', alignItems:'flex-start', gap:5 }}>
            <div style={{ fontWeight:600 }}>⚠️ What happens if I miss a deadline?</div>
            <div style={{ fontSize:12, lineHeight:1.6 }}>HMRC charges £200 for each late quarterly filing. After 3 months they add £10/day on top. Quarterly sends you reminders so you never get there.</div>
          </div>
        </div>

        {/* Payment deadlines */}
        <div>
          <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', marginBottom:14 }}>
            <div style={{ fontSize:13, fontWeight:600, color:'var(--amber)', display:'flex', alignItems:'center', gap:7 }}>
              <CreditCard size={14} /> Payment Deadlines
            </div>
            <button
              onClick={() => setShowPoaExplainer(!showPoaExplainer)}
              style={{ background:'none', border:'none', cursor:'pointer', display:'flex', alignItems:'center', gap:5, fontSize:12, color:'var(--slate)' }}
            >
              <HelpCircle size={13} /> What is payment on account?
            </button>
          </div>

          {showPoaExplainer && (
            <div className="card fade-in" style={{ marginBottom:14, border:'1px solid rgba(245,166,35,.2)', background:'var(--amber-glow)' }}>
              <div style={{ fontWeight:600, fontSize:13, color:'var(--amber)', marginBottom:8 }}>💡 Payment on Account — explained simply</div>
              <div style={{ fontSize:13, color:'var(--slate)', lineHeight:1.7 }}>
                If your tax bill last year was over £1,000 and less than 80% was already deducted at source, HMRC asks you to pre-pay next year's tax bill in two instalments — 31 January and 31 July.
              </div>
              <div style={{ marginTop:10, fontSize:13, color:'var(--slate)', lineHeight:1.7 }}>
                Each payment is 50% of your previous year's bill. So if you owed £4,000 last year, you'd pay £2,000 in January and £2,000 in July.
              </div>
              <div style={{ marginTop:10, padding:'10px 12px', background:'rgba(245,166,35,.08)', borderRadius:7, fontSize:12, color:'var(--amber)' }}>
                ⚠️ <strong>Does this apply to you?</strong> If this is your first year self-employed, you won't have payments on account yet. If you've filed before and your bill was over £1,000, you almost certainly do.
              </div>
              <div style={{ marginTop:10, fontSize:12, color:'var(--slate-dim)' }}>
                Quarterly will calculate your exact payment on account amounts once your final tax position is confirmed.
              </div>
            </div>
          )}

          <div className="card">
            {deadlines.filter(d => d.type === 'payment').map((d, i) => {
              const s = status(d.date)
              const cfg = statusConfig[s]
              const days = daysUntil(d.date)
              return (
                <div key={d.q} className="timeline-item">
                  <div className="timeline-dot" style={{ background:cfg.dot, marginTop:5 }} />
                  <div style={{ flex:1 }}>
                    <div style={{ display:'flex', justifyContent:'space-between', alignItems:'flex-start', marginBottom:4 }}>
                      <div style={{ fontWeight:600, fontSize:14 }}>{d.label}</div>
                      <span className={`badge ${cfg.badge}`} style={{ fontSize:10 }}>
                        {s === 'past' ? 'Paid' : `${days}d`}
                      </span>
                    </div>
                    <div style={{ fontSize:12, color:'var(--slate)', marginBottom:2 }}>{d.period}</div>
                    <div style={{ fontSize:12 }}>
                      <span style={{ color:'var(--slate)' }}>Due: </span>
                      <span style={{ color: s === 'urgent' ? 'var(--red)' : s === 'past' ? 'var(--slate-dim)' : 'var(--white)', fontWeight:600 }}>{d.deadline}</span>
                    </div>
                    {d.q === 'POA1' || d.q === 'POA2' ? (
                      <div style={{ fontSize:11, color:'var(--slate-dim)', marginTop:4, display:'flex', alignItems:'center', gap:4 }}>
                        <Info size={10} /> Only applies if last year's tax bill exceeded £1,000
                      </div>
                    ) : null}
                  </div>
                </div>
              )
            })}
          </div>

          {/* Year overview strip */}
          <div className="card" style={{ marginTop:14, padding:'16px 18px' }}>
            <div style={{ fontWeight:600, fontSize:13, marginBottom:12 }}>2026/27 at a glance</div>
            <div style={{ position:'relative', height:6, background:'var(--navy-border)', borderRadius:3, marginBottom:20 }}>
              {[
                { pct:10, label:'Q1', deadline:'7 Aug 2026' },
                { pct:35, label:'Q2', deadline:'7 Nov 2026' },
                { pct:60, label:'Q3', deadline:'7 Feb 2027' },
                { pct:85, label:'Q4', deadline:'7 May 2027' },
              ].map((m, i) => {
                const d = deadlines.find(dl => dl.q === m.label)
                const s = d ? status(d.date) : 'future'
                const dotColor = s === 'past' ? 'var(--teal)' : s === 'urgent' ? 'var(--red)' : s === 'upcoming' ? 'var(--amber)' : 'var(--navy-border)'
                const labelColor = s === 'past' ? 'var(--teal)' : s === 'urgent' ? 'var(--red)' : s === 'upcoming' ? 'var(--amber)' : 'var(--slate-dim)'
                const labelText = s === 'past' ? `${m.label} filed` : s === 'urgent' ? `${m.label} due soon` : s === 'upcoming' ? `${m.label} upcoming` : `${m.label} scheduled`
                return (
                  <div key={i} style={{ position:'absolute', left:`${m.pct}%`, top:'50%', transform:'translate(-50%,-50%)', width:12, height:12, borderRadius:'50%', background:dotColor, border:'2px solid var(--navy-card)' }}>
                    <div style={{ position:'absolute', top:14, left:'50%', transform:'translateX(-50%)', fontSize:9, color:labelColor, whiteSpace:'nowrap', fontWeight:600 }}>{labelText}</div>
                  </div>
                )
              })}
              {/* Progress bar — fills based on how many quarters are past */}
              {(() => {
                const submissionDeadlines = deadlines.filter(d => d.type === 'submission')
                const pastCount = submissionDeadlines.filter(d => status(d.date) === 'past').length
                const pct = (pastCount / 4) * 85
                return <div style={{ width:`${pct}%`, height:'100%', background:'var(--teal)', borderRadius:3, transition:'width .4s' }} />
              })()}
            </div>
            <div style={{ fontSize:11, color:'var(--slate-dim)', marginTop:8 }}>Tax year: 6 April 2026 — 5 April 2027 · MTD ITSA 2026/27</div>
          </div>
        </div>
      </div>
    </div>
  )
}
